'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useForumStore } from '@/store/forumStore';
import { ForumTopic } from '@/types';
import { highlightText } from '@/utils';
import { MessageSquare, ThumbsUp, Clock, Send, X, Trash2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { ReportButton } from '@/components/ui/ReportButton';
import { CrisisModal, InappropriateLanguageModal, InappropriateLanguageBanner, Modal, ValidationFooter, ERROR_BORDER_CLASS } from '@/components/ui';
import { useContentModeration } from '@/hooks/useContentModeration';
import { linkifyText } from '@/lib/linkify';
import { forumAnchorId } from './forumAnchor';

interface GroupTopicCardProps {
  topic: ForumTopic;
  groupId?: number;
  eventDocumentId?: string;
  volunteerDocumentId?: string;
  searchQuery: string;
  isMember: boolean;
  isGroupAdmin?: boolean;
}

export function GroupTopicCard({ topic, groupId, eventDocumentId, volunteerDocumentId, searchQuery, isMember, isGroupAdmin = false }: GroupTopicCardProps) {
  const { t } = useLanguage();

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return t.time.justNow;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}${t.time.minutesAgo}`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}${t.time.hoursAgo}`;
    return `${Math.floor(diffInSeconds / 86400)}${t.time.daysAgo}`;
  };
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replySubmitAttempted, setReplySubmitAttempted] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ documentId: string; authorName: string; parentReplyDocId: string } | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');
  const [editingLoading, setEditingLoading] = useState(false);

  // Per-field content moderation (MWBI-875). Each composer field owns its own
  // hook instance so detection/blocking stays isolated: profanity in the reply
  // box must not disable the edit-save button and vice versa. Crisis stays
  // informational (never blocks); inappropriate language blocks its own button.
  const replyModeration = useContentModeration(replyText);
  const editModeration = useContentModeration(editingReplyContent);

  const { repliesByTopic, fetchReplies, toggleLike, hasUserLiked, toggleReplyLike, hasUserLikedReply, createReply, updateReply, deleteReply, deleteTopic } = useForumStore();
  const { currentUser } = useAuth();
  const content = (topic.content?.[0] as { children?: { text?: string }[] })?.children?.[0]?.text || '';
  const replies = repliesByTopic[topic.documentId] ?? [];
  const hasFetchedReplies = Object.prototype.hasOwnProperty.call(repliesByTopic, topic.documentId);
  const replyCount = topic.replies ?? (topic as unknown as { forum_replies?: { count?: number } }).forum_replies?.count ?? 0;
  const displayReplyCount = replies.length > 0 ? replies.length : replyCount;
  const isLiked = currentUser
    ? hasUserLiked(topic.documentId, currentUser.id, groupId, eventDocumentId, volunteerDocumentId)
    : false;

  const handleToggleReplies = () => {
    if (!expanded && !hasFetchedReplies) fetchReplies(topic.documentId);
    setExpanded(!expanded);
  };

  // Auto-expand when this topic is the one named in the URL hash (MWBI-910).
  // Group-activity emails deep-link to /groups/<id>?tab=forum#forum-<documentId>;
  // opening the discussion should show the full thread, not a collapsed card.
  // Mirrors the per-card effect in frontend/src/app/forums/page.tsx; the parent
  // ForumTab handles the scroll/highlight/focus + hash cleanup. Uses the card's
  // hasFetchedReplies idiom (not replies.length) so a topic with zero replies
  // isn't re-fetched on every render.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Compare against the ENCODED anchor so the URL hash (which the email
    // percent-encodes) matches the rendered id for any documentId (MWBI-910).
    if (window.location.hash === `#${forumAnchorId(topic.documentId)}`) {
      if (!hasFetchedReplies) fetchReplies(topic.documentId);
      setExpanded(true);
    }
  }, [topic.documentId, fetchReplies, hasFetchedReplies]);

  const handleSendReply = async () => {
    if (!currentUser || sending || !isMember) return;
    if (replyModeration.hasInappropriate) return;
    if (!replyText.trim()) {
      setReplySubmitAttempted(true);
      return;
    }
    setSending(true);
    const success = await createReply({
      topicDocumentId: topic.documentId,
      content: replyText.trim(),
      parentReplyDocumentId: replyingTo?.documentId,
    });
    if (success) { setReplyText(''); setReplyingTo(null); setReplySubmitAttempted(false); }
    setSending(false);
  };

  const replyEmpty = replySubmitAttempted && !replyText.trim();

  const handleStartEditReply = (replyDocumentId: string, content: string) => {
    setEditingReplyId(replyDocumentId);
    setEditingReplyContent(content);
  };

  const handleCancelEditReply = () => {
    setEditingReplyId(null);
    setEditingReplyContent('');
  };

  const handleSaveEditReply = async () => {
    if (!editingReplyId || !editingReplyContent.trim() || editingLoading) return;
    if (editModeration.hasInappropriate) return;
    setEditingLoading(true);
    const success = await updateReply(topic.documentId, editingReplyId, editingReplyContent.trim());
    setEditingLoading(false);
    if (success) {
      handleCancelEditReply();
    }
  };

  const [pendingDeleteReplyId, setPendingDeleteReplyId] = useState<string | null>(null);

  const handleDeleteReply = (replyDocumentId: string) => {
    setPendingDeleteReplyId(replyDocumentId);
  };

  const confirmDeleteReply = async () => {
    if (!pendingDeleteReplyId) return;
    const replyId = pendingDeleteReplyId;
    setPendingDeleteReplyId(null);
    const success = await deleteReply(topic.documentId, replyId);
    if (success && editingReplyId === replyId) {
      handleCancelEditReply();
    }
  };

  const [deletingTopic, setDeletingTopic] = useState(false);
  const [showDeleteTopicModal, setShowDeleteTopicModal] = useState(false);

  const handleDeleteTopic = () => {
    setShowDeleteTopicModal(true);
  };

  const confirmDeleteTopic = async () => {
    setShowDeleteTopicModal(false);
    setDeletingTopic(true);
    await deleteTopic(topic.documentId, groupId, eventDocumentId, volunteerDocumentId);
    setDeletingTopic(false);
  };

  const canDeleteTopic = !!currentUser && (currentUser.id === topic.author?.id || isGroupAdmin);

  const avatarUrl = topic.author?.avatarUrl || null;
  const topLevelReplies = replies.filter(r => !r.parentReply);
  const getChildReplies = (parentDocId: string) => replies.filter(r => r.parentReply?.documentId === parentDocId);

  return (
    <div
      id={forumAnchorId(topic.documentId)}
      data-testid={`group-topic-${topic.documentId}`}
      tabIndex={-1}
      className="bg-white border border-border rounded-lg p-4 mb-4 transition-shadow hover:shadow-md scroll-mt-4"
    >
      <CrisisModal
        open={replyModeration.showCrisisModal}
        onClose={replyModeration.dismissCrisisModal}
        testIdPrefix="group-topic-reply-crisis"
      />
      <CrisisModal
        open={editModeration.showCrisisModal}
        onClose={editModeration.dismissCrisisModal}
        testIdPrefix="group-topic-edit-crisis"
      />
      <InappropriateLanguageModal
        open={replyModeration.showInappropriateModal}
        onClose={replyModeration.dismissInappropriateModal}
        testIdPrefix="group-topic-reply-inappropriate"
      />
      <InappropriateLanguageModal
        open={editModeration.showInappropriateModal}
        onClose={editModeration.dismissInappropriateModal}
        testIdPrefix="group-topic-edit-inappropriate"
      />
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-background-secondary flex items-center justify-center text-foreground-muted shrink-0 overflow-hidden">
            {avatarUrl ? <Image src={avatarUrl} alt={topic.author?.displayName ?? t.common.anonymous} width={40} height={40} className="w-full h-full object-contain" unoptimized /> : (topic.author?.displayName ?? t.common.anonymous).charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground">{highlightText(topic.title, searchQuery)}</h3>
            <div className="flex items-center gap-2 text-sm text-foreground-muted mt-1 flex-wrap">
              <span>{topic.author?.displayName ?? t.common.anonymous}</span>
              <span>•</span><Clock size={14} /><span>{getTimeAgo(topic.createdAt)}</span>
              {topic.category && <><span>•</span><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-background-secondary text-foreground-muted border border-border">{highlightText(topic.category.name, searchQuery)}</span></>}
            </div>
            <p className="text-foreground-muted mt-3 whitespace-pre-wrap" data-testid={`group-topic-body-${topic.documentId}`}>{linkifyText(content)}</p>
            <div className="flex gap-6 mt-4 text-sm">
              <button onClick={handleToggleReplies} className="flex items-center gap-2 text-primary hover:underline" data-testid={`group-topic-replies-toggle-${topic.documentId}`}>
                <MessageSquare size={16} /><span className="font-medium">{displayReplyCount} {displayReplyCount === 1 ? t.groupTabs.reply : t.groupTabs.replies}</span>
              </button>
              <button onClick={() => isMember && currentUser && toggleLike(topic.documentId, groupId, eventDocumentId, volunteerDocumentId)} disabled={!isMember || !currentUser}
                className={`flex items-center gap-2 transition-colors ${!isMember || !currentUser ? 'text-foreground-muted cursor-not-allowed opacity-50' : isLiked ? 'text-primary' : 'text-foreground-muted hover:text-primary'}`}>
                <ThumbsUp size={16} className={isLiked ? 'fill-current' : ''} /><span>{topic.likes} {t.common.likes}</span>
              </button>
              {currentUser && topic.author && currentUser.id !== topic.author.id && (
                <ReportButton contentType="forum" contentId={topic.documentId} contentTitle={topic.title} />
              )}
            </div>

          {expanded && (
            <div className="mt-4">
              {!replyingTo && isMember && (
                <div className="mb-4">
                  {currentUser && <p className="text-sm font-medium text-foreground mb-1">{currentUser.displayName}</p>}
                  {replyModeration.hasInappropriate && (
                    <div className="mb-2">
                      <InappropriateLanguageBanner testIdPrefix="group-topic-card-reply-inappropriate" />
                    </div>
                  )}
                  <div className="relative">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendReply())}
                      placeholder={t.comments.shareThoughts}
                      disabled={sending}
                      rows={2}
                      data-testid="group-topic-card-reply-textarea"
                      className={`w-full px-4 py-2 pr-10 border rounded-lg bg-background text-sm focus:outline-none focus:border-primary disabled:opacity-50 resize-none ${replyEmpty ? ERROR_BORDER_CLASS : 'border-border'}`}
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={sending || replyModeration.hasInappropriate}
                      data-testid="group-topic-card-reply-send-btn"
                      className="absolute right-3 bottom-3 text-foreground-muted hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <ValidationFooter
                      value={replyText}
                      required
                      showRequiredError={replySubmitAttempted}
                      requiredMessage={t.errors.forumReplyRequired}
                      testIdPrefix="group-topic-card-reply"
                    />
                    <span className={`text-xs ${replyText.length >= 450 ? 'text-warning' : 'text-foreground-muted'}`}>{replyText.length}/500</span>
                  </div>
                </div>
              )}

              {replies.length === 0 ? <p className="text-sm text-foreground-muted text-center py-2">{t.groupTabs.noRepliesYet}</p> : (
                <div className="space-y-3">
                  {topLevelReplies.map((reply) => {
                    const isReplyLiked = currentUser ? hasUserLikedReply(topic.documentId, reply.documentId, currentUser.id) : false;
                    const childReplies = getChildReplies(reply.documentId);

                    return (
                      <div key={reply.id}>
                        <div>
                          <div className="flex items-center gap-2 mb-1"><span className="font-medium text-sm text-foreground">{reply.author?.displayName || t.common.anonymous}</span><span className="text-xs text-foreground-muted">{getTimeAgo(reply.createdAt)}</span></div>
                            {editingReplyId === reply.documentId ? (
                              <div className="mt-1 space-y-2">
                                {editModeration.hasInappropriate && (
                                  <InappropriateLanguageBanner testIdPrefix="group-topic-card-edit-inappropriate" />
                                )}
                                <textarea
                                  value={editingReplyContent}
                                  onChange={(e) => setEditingReplyContent(e.target.value.slice(0, 500))}
                                  rows={3}
                                  data-testid="group-topic-card-edit-textarea"
                                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary resize-none"
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={handleSaveEditReply}
                                    disabled={!editingReplyContent.trim() || editingLoading || editModeration.hasInappropriate}
                                    data-testid="group-topic-card-edit-save-btn"
                                    className="px-2 py-1 text-xs font-medium text-white bg-foreground rounded hover:opacity-90 disabled:opacity-50"
                                  >
                                    {editingLoading ? t.common.saving : t.common.save}
                                  </button>
                                  <button
                                    onClick={handleCancelEditReply}
                                    disabled={editingLoading}
                                    className="px-2 py-1 text-xs font-medium text-foreground-muted hover:text-foreground"
                                  >
                                    {t.common.cancel}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-foreground bg-background-secondary rounded-lg px-3 py-2 mt-1 inline-block max-w-full whitespace-pre-wrap" data-testid={`group-topic-reply-body-${reply.documentId}`}>{linkifyText(reply.content)}</p>
                            )}
                            <div className="flex items-center gap-3 mt-1">
                              <button onClick={() => isMember && currentUser && toggleReplyLike(topic.documentId, reply.documentId)} disabled={!isMember || !currentUser} className={`flex items-center gap-1 text-xs transition-colors ${!isMember || !currentUser ? 'text-foreground-muted cursor-not-allowed opacity-50' : isReplyLiked ? 'text-primary' : 'text-foreground-muted hover:text-primary'}`}>
                                <ThumbsUp size={12} className={isReplyLiked ? 'fill-current' : ''} /><span>{reply.likes || 0}</span>
                              </button>
                              {isMember && <button onClick={() => currentUser && setReplyingTo({ documentId: reply.documentId, authorName: reply.author?.displayName || t.common.anonymous, parentReplyDocId: reply.documentId })} disabled={!currentUser} className="flex items-center gap-1 text-xs text-foreground-muted hover:text-primary disabled:opacity-50"><MessageSquare size={12} /><span>{t.groupTabs.reply}</span></button>}
                              {currentUser && currentUser.id === reply.author?.id && editingReplyId !== reply.documentId && (
                                <>
                                  <button onClick={() => handleStartEditReply(reply.documentId, reply.content)} className="text-xs text-foreground-muted hover:text-primary">
                                    {t.common.edit}
                                  </button>
                                  <button onClick={() => handleDeleteReply(reply.documentId)} className="text-xs text-red-600 hover:text-red-700">
                                    {t.common.delete}
                                  </button>
                                </>
                              )}
                            </div>
                        </div>

                        {childReplies.length > 0 && (
                          <div className="ml-11 mt-2 space-y-2 border-l-2 border-border pl-4">
                            {childReplies.map((child) => {
                              const isChildLiked = currentUser ? hasUserLikedReply(topic.documentId, child.documentId, currentUser.id) : false;
                              return (
                                <div key={child.id}>
                                  <div className="flex items-center gap-2 mb-1"><span className="font-medium text-xs text-foreground">{child.author?.displayName || t.common.anonymous}</span><span className="text-[10px] text-foreground-muted">{getTimeAgo(child.createdAt)}</span></div>
                                    {editingReplyId === child.documentId ? (
                                      <div className="mt-1 space-y-2">
                                        {editModeration.hasInappropriate && (
                                          <InappropriateLanguageBanner testIdPrefix="group-topic-card-edit-child-inappropriate" />
                                        )}
                                        <textarea
                                          value={editingReplyContent}
                                          onChange={(e) => setEditingReplyContent(e.target.value.slice(0, 500))}
                                          rows={3}
                                          className="w-full px-2 py-1.5 border border-border rounded-lg bg-background text-xs focus:outline-none focus:border-primary resize-none"
                                        />
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={handleSaveEditReply}
                                            disabled={!editingReplyContent.trim() || editingLoading || editModeration.hasInappropriate}
                                            className="px-2 py-1 text-[10px] font-medium text-white bg-foreground rounded hover:opacity-90 disabled:opacity-50"
                                          >
                                            {editingLoading ? t.common.saving : t.common.save}
                                          </button>
                                          <button
                                            onClick={handleCancelEditReply}
                                            disabled={editingLoading}
                                            className="px-2 py-1 text-[10px] font-medium text-foreground-muted hover:text-foreground"
                                          >
                                            {t.common.cancel}
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-foreground bg-background-secondary rounded-lg px-2 py-1.5 mt-1 inline-block max-w-full whitespace-pre-wrap" data-testid={`group-topic-reply-child-body-${child.documentId}`}>{linkifyText(child.content)}</p>
                                    )}
                                    <div className="flex items-center gap-3 mt-1">
                                      <button onClick={() => isMember && currentUser && toggleReplyLike(topic.documentId, child.documentId)} disabled={!isMember || !currentUser} className={`flex items-center gap-1 text-[10px] transition-colors ${!isMember || !currentUser ? 'text-foreground-muted cursor-not-allowed opacity-50' : isChildLiked ? 'text-primary' : 'text-foreground-muted hover:text-primary'}`}>
                                        <ThumbsUp size={10} className={isChildLiked ? 'fill-current' : ''} /><span>{child.likes || 0}</span>
                                      </button>
                                      {currentUser && currentUser.id === child.author?.id && editingReplyId !== child.documentId && (
                                        <>
                                          <button onClick={() => handleStartEditReply(child.documentId, child.content)} className="text-[10px] text-foreground-muted hover:text-primary">
                                            {t.common.edit}
                                          </button>
                                          <button onClick={() => handleDeleteReply(child.documentId)} className="text-[10px] text-red-600 hover:text-red-700">
                                            {t.common.delete}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {replyingTo?.parentReplyDocId === reply.documentId && (
                          <div className="ml-11 mt-2 border-l-2 border-primary pl-4">
                            <div className="flex items-center gap-2 mb-2 text-xs text-primary">
                              <span>{t.groupTabs.replyingTo} @{replyingTo.authorName}</span>
                              <button onClick={() => { setReplyingTo(null); setReplyText(''); }} className="text-foreground-muted hover:text-foreground"><X size={14} /></button>
                            </div>
                            <div>
                              {currentUser && <p className="text-xs font-medium text-foreground mb-1">{currentUser.displayName}</p>}
                              {replyModeration.hasInappropriate && (
                                <div className="mb-2">
                                  <InappropriateLanguageBanner testIdPrefix="group-topic-card-nested-reply-inappropriate" />
                                </div>
                              )}
                              <div className="relative">
                                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value.slice(0, 500))} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendReply())} placeholder={`${t.groupTabs.replyTo} ${replyingTo.authorName}...`} disabled={sending} rows={2} className="w-full px-3 py-2 pr-8 border border-border rounded-lg bg-background text-xs focus:outline-none focus:border-primary disabled:opacity-50 resize-none" autoFocus />
                                <button onClick={handleSendReply} disabled={!replyText.trim() || sending || replyModeration.hasInappropriate} className="absolute right-2 bottom-2 text-foreground-muted hover:text-primary disabled:opacity-50"><Send size={14} /></button>
                              </div>
                              <div className="flex justify-end mt-1"><span className={`text-[10px] ${replyText.length >= 450 ? 'text-warning' : 'text-foreground-muted'}`}>{replyText.length}/500</span></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
        {canDeleteTopic && (
          <button
            onClick={handleDeleteTopic}
            disabled={deletingTopic}
            className="shrink-0 p-1 text-foreground-muted hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t.common.delete}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <Modal
        isOpen={showDeleteTopicModal}
        onClose={() => setShowDeleteTopicModal(false)}
        title={t.groupTabs.deleteTopicTitle}
        subtitle={t.groupTabs.deleteTopicConfirm}
        footer={
          <>
            <button
              onClick={() => setShowDeleteTopicModal(false)}
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              {t.common.cancel}
            </button>
            <button
              onClick={confirmDeleteTopic}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
            >
              {t.common.delete}
            </button>
          </>
        }
      >
        <></>
      </Modal>

      <Modal
        isOpen={!!pendingDeleteReplyId}
        onClose={() => setPendingDeleteReplyId(null)}
        title={t.groupTabs.deleteReplyTitle}
        subtitle={t.groupTabs.deleteReplyConfirm}
        footer={
          <>
            <button
              onClick={() => setPendingDeleteReplyId(null)}
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              {t.common.cancel}
            </button>
            <button
              onClick={confirmDeleteReply}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
            >
              {t.common.delete}
            </button>
          </>
        }
      >
        <></>
      </Modal>
    </div>
  );
}
